/**
 * Build-time feature flags.
 *
 * These are compile-time constants injected by `define` and statically
 * replaced at build, so guarded branches are dead-code-eliminated from the
 * bundle they don't apply to. They are NOT runtime-togglable — flipping one
 * requires a rebuild.
 *
 * The per-mode VALUES live in `frontend/build-flags.js`, which both
 * `vite.config.js` and `vitest.config.js` feed to `define` — so the test suite
 * exercises the same flags a shipped build carries. (Until 2026-08-28 each
 * flag here carried a `typeof __X__ !== 'undefined' ? __X__ : <fallback>`
 * escape hatch for vitest, and both fallbacks had drifted to the INVERSE of
 * every shipped build. The shared map replaced them; there is no fallback to
 * drift any more.) This file documents INTENT; `build-flags.js` is what a
 * given build actually ships.
 *
 * A test that needs the branch a build does NOT ship opts in explicitly with
 * `vi.mock('.../config/featureFlags.js', ...)`. `vi.stubGlobal` will not work:
 * `define` replaces the identifier textually at transform time, so there is no
 * global left to stub.
 */

/**
 * Guided cell-selection flow (BA's AI-First two-door landing — `CellSelectionEntry`
 * — plus the "Start a guided selection" hint on the grid view).
 *
 * OFF in every build since 2026-08-27 (TS) — cell selection uses the classic,
 * non-AI path: `/cell-selection` redirects straight into the Advanced
 * filter+ranking wizard and the grid's "Start a guided selection" hint is
 * dead-code-eliminated. (History: dev+staging-only until 2026-08-10, then on
 * everywhere, then off again.)
 *
 * Deliberately decoupled from Ion visibility (`ionHidden`): Ion can be live
 * for PRO/DEMO users at /assistant while the guided selection door stays
 * hidden. This flag governs only how cell SELECTION starts.
 */
export const GUIDED_SELECTION_ENABLED = __GUIDED_SELECTION_ENABLED__

/**
 * Ion-Home as the default post-login landing — the `/` redirect and the
 * public-auth-page `redirectTo` targets (login/register/forgot-password/
 * reset-password/verify-email) point at `/assistant` instead of `/explorer`
 * when this is on (see `core/router.jsx`). `AssistantRouteGuard` still
 * bounces FREE/`ionHidden` users to `/explorer` regardless — this flag only
 * changes where an ENTITLED user lands.
 *
 * ON for staging and production builds since 2026-08-10 (BA released Ion as
 * the front door); OFF for dev, where work usually starts from the explorer.
 * Baked from the Vite build `mode` rather than a runtime env var, so it cannot
 * drift with a stray `.env` value.
 *
 * Scope note (2026-08-28): this flag governs the LANDING TARGET only. It used
 * to double as a "temporary production hide" on the /assistant route and the
 * ION nav tab — both gates were inert in every deployed build (the flag is
 * true in staging and production) and have been deleted. Hiding Ion is the job
 * of the server-derived `ionHidden` signal alone; turning this flag off will
 * NOT hide the route or the tab.
 */
export const ION_HOME_DEFAULT_ENABLED = __ION_HOME_DEFAULT_ENABLED__
